/**
 * Coord-event trigger dispatch (H5 / MVP PR 13a).
 *
 * Cartridge `coord_event` is a (x, y) → scriptRef binding that fires
 * when the player steps onto the tile. v0.1's first consumer is the
 * Oak intercept on Route 1 (PR 13b). The dispatcher itself is one-shot-
 * agnostic — it fires the matching scriptRef on every step-onto-tile;
 * the handler self-gates via runState flags (e.g., FLAG_OAK_INTRO_DONE).
 *
 * Pure helper, no Phaser deps. WorldScene.advanceStep imports findTrigger
 * AtTile and dispatches via scriptRunner.dispatchScript().
 */

export interface CoordTriggerLike {
  /** Tile-local X. */
  x: number;
  /** Tile-local Y. */
  y: number;
  /** Script binding; key into scriptRunner's registry or dialogue table. */
  scriptRef: string;
  /** Cartridge variable name that gates the trigger. v0.1 honors only
   *  '0' (no gate); var-state triggers are deferred. Future MVP work
   *  can add a getVar(name) === varValue check before dispatching. */
  var: string;
  /** Cartridge variable value required for the gate to open. */
  varValue: number;
}

/** Find the first coord-event trigger whose tile matches the player's
 *  landing tile AND whose cartridge `var` gate is currently open.
 *
 *  Gating semantics match cartridge:
 *  - `var === '0'`: ungated; always eligible.
 *  - `var === 'VAR_X', varValue: N`: eligible iff `getVar('VAR_X') === N`.
 *    Cartridge defaults unset vars to 0, so a trigger with `varValue: 0`
 *    is eligible by default until a handler does `setVar('VAR_X', !=0)`.
 *
 *  The optional `getVar` callback lets the runtime plug in
 *  `runState.getVar`-style lookup without coupling this pure module to
 *  runState. When `getVar` is omitted, only `var === '0'` triggers
 *  match (useful for tests that don't need var simulation). */
export function findTriggerAtTile(
  tile: { x: number; y: number },
  triggers: readonly CoordTriggerLike[],
  getVar?: (name: string) => number,
): CoordTriggerLike | null {
  for (const t of triggers) {
    if (t.x !== tile.x || t.y !== tile.y) continue;
    if (t.var === '0') return t;
    if (!getVar) continue; // var-gated, no resolver provided
    if (getVar(t.var) === t.varValue) return t;
  }
  return null;
}
